import { LoaderCircle, Sparkles } from "lucide-react";
import { isCarouselSocialPostType } from "@runcheetah/shared";

import {
  CodeText,
  InfoTooltip,
  Notice,
  SectionLabel,
  SegmentedControl,
  Select,
  Textarea,
} from "@/components/common";
import { Button } from "@runcheetah/ui";
import { Badge } from "@runcheetah/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@runcheetah/ui";

import {
  POST_TYPE_OPTIONS,
  type PlannerBrandPresence,
  type PlannerGenerationMode,
  type PlannerPostType,
} from "../constants";

export interface PlannerLayoutTemplateOption {
  templateId: string;
  name: string;
  description: string;
}

export interface PlannerCarouselFormatOption {
  key: string;
  name: string;
  minSlides: number;
  maxSlides: number;
}

const GENERATION_MODE_OPTIONS = [
  { value: "auto", label: "Runtime default" },
  { value: "template", label: "Template" },
  { value: "freestyle", label: "Freestyle" },
] as const satisfies ReadonlyArray<{ value: PlannerGenerationMode; label: string }>;

const BRAND_PRESENCE_OPTIONS = [
  { value: "branded", label: "Branded" },
  { value: "non_branded", label: "Unbranded" },
] as const satisfies ReadonlyArray<{ value: PlannerBrandPresence; label: string }>;

export function PlannerRunForm({
  prompt,
  postType,
  brandPresence,
  generationMode,
  layoutTemplateId,
  carouselFormatKey,
  layoutTemplateOptions,
  carouselFormatOptions,
  layoutTemplatesLoading,
  carouselFormatsLoading,
  layoutTemplatesError,
  carouselFormatsError,
  onPromptChange,
  onPostTypeChange,
  onBrandPresenceChange,
  onGenerationModeChange,
  onLayoutTemplateIdChange,
  onCarouselFormatKeyChange,
  disabled,
  isPending,
  error,
  brandStatus,
  onSubmit,
}: {
  prompt: string;
  postType: PlannerPostType;
  brandPresence: PlannerBrandPresence;
  generationMode: PlannerGenerationMode;
  layoutTemplateId: string;
  carouselFormatKey: string;
  layoutTemplateOptions: readonly PlannerLayoutTemplateOption[];
  carouselFormatOptions: readonly PlannerCarouselFormatOption[];
  layoutTemplatesLoading: boolean;
  carouselFormatsLoading: boolean;
  layoutTemplatesError: string | null;
  carouselFormatsError: string | null;
  onPromptChange: (value: string) => void;
  onPostTypeChange: (value: PlannerPostType) => void;
  onBrandPresenceChange: (value: PlannerBrandPresence) => void;
  onGenerationModeChange: (value: PlannerGenerationMode) => void;
  onLayoutTemplateIdChange: (value: string) => void;
  onCarouselFormatKeyChange: (value: string) => void;
  disabled: boolean;
  isPending: boolean;
  error: string | null;
  brandStatus: string;
  onSubmit: () => void;
}) {
  const isCarousel = isCarouselSocialPostType(postType);
  const templateModeUnavailable =
    !isCarousel && generationMode === "template" && layoutTemplateOptions.length === 0;
  const carouselFormatsUnavailable = isCarousel && carouselFormatOptions.length === 0;
  const submitDisabled =
    disabled ||
    prompt.trim().length === 0 ||
    (isCarousel && (carouselFormatsLoading || carouselFormatsUnavailable)) ||
    (!isCarousel &&
      generationMode === "template" &&
      (layoutTemplatesLoading || templateModeUnavailable));

  return (
    <Card className="py-5">
      <CardHeader>
        <CardTitle>New Generation Attempt</CardTitle>
        <CardDescription>
          Use this expert tool for one-off creative experiments outside the campaign workflow.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
            Platform: Instagram
          </Badge>
          <span className="inline-flex items-center gap-1.5">
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
              Variants: 1
            </Badge>
            <InfoTooltip
              label="Explain variant count"
              content="This tool currently queues one creative output per attempt. Multi-variant generation will be exposed as a separate control when it is ready."
            />
          </span>
        </div>

        <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
          <div className="flex items-center gap-2">
            <SectionLabel>Brand presence</SectionLabel>
            <InfoTooltip
              label="Explain brand presence"
              content="Branded attempts may use direct brand identity, product cues, and brand CTAs. Unbranded attempts use category and audience context while avoiding direct brand or product promotion."
            />
          </div>
          <SegmentedControl
            options={BRAND_PRESENCE_OPTIONS}
            value={brandPresence}
            onChange={onBrandPresenceChange}
            className="rounded-xl sm:w-fit"
            buttonClassName="rounded-lg"
          />
        </div>

        <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
          <SectionLabel>Post type</SectionLabel>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" htmlFor="planner-post-type">
              Target format
            </label>
            <InfoTooltip
              label="Explain target format"
              content="Sets the social surface and canvas size for this attempt, for example Instagram feed portrait, square carousel, story, or TikTok portrait."
            />
          </div>
          <Select
            id="planner-post-type"
            value={postType}
            onChange={(event) => onPostTypeChange(event.target.value as PlannerPostType)}
          >
            {POST_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} ({option.description})
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            Variant count is fixed to <CodeText tone="default">1</CodeText> until multi-variant
            planning is enabled.
          </p>
        </div>

        {isCarousel ? (
          <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
            <SectionLabel>Carousel story format</SectionLabel>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" htmlFor="planner-carousel-format">
                Narrative format
              </label>
              <InfoTooltip
                label="Explain carousel narrative format"
                content="Optionally pins this carousel to one active narrative structure. Leave it on auto to let the story planner choose the best fit."
              />
            </div>
            <Select
              id="planner-carousel-format"
              value={carouselFormatKey}
              disabled={disabled || carouselFormatsLoading || carouselFormatsUnavailable}
              onChange={(event) => onCarouselFormatKeyChange(event.target.value)}
            >
              <option value="">Auto-pick narrative format</option>
              {carouselFormatOptions.map((format) => (
                <option key={format.key} value={format.key}>
                  {format.name} ({format.minSlides}-{format.maxSlides} slides)
                </option>
              ))}
            </Select>
            {carouselFormatsLoading ? (
              <p className="text-xs text-muted-foreground">Loading active carousel formats...</p>
            ) : null}
            {carouselFormatsError ? <Notice tone="danger">{carouselFormatsError}</Notice> : null}
            {!carouselFormatsLoading && carouselFormatsUnavailable ? (
              <Notice tone="warning">No active carousel narrative formats are available.</Notice>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <SectionLabel>Generation strategy</SectionLabel>
                <InfoTooltip
                  label="Explain generation strategy"
                  content="Runtime default preserves the brand/runtime flag behavior. Template forces published layout templates. Freestyle skips templates for unconstrained scene planning."
                />
              </div>
              <SegmentedControl
                options={GENERATION_MODE_OPTIONS}
                value={generationMode}
                onChange={(value) => {
                  onGenerationModeChange(value);
                  if (value === "freestyle") {
                    onLayoutTemplateIdChange("");
                  }
                }}
                className="rounded-xl"
                buttonClassName="rounded-lg"
              />
            </div>

            {generationMode === "template" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium" htmlFor="planner-layout-template">
                    Layout template
                  </label>
                  <InfoTooltip
                    label="Explain layout template"
                    content="Optionally pins this single-post attempt to one compatible published template. Auto-pick still forces template mode but lets the planner choose."
                  />
                </div>
                <Select
                  id="planner-layout-template"
                  value={layoutTemplateId}
                  disabled={disabled || layoutTemplatesLoading || templateModeUnavailable}
                  onChange={(event) => onLayoutTemplateIdChange(event.target.value)}
                >
                  <option value="">Auto-pick compatible template</option>
                  {layoutTemplateOptions.map((template) => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.name}
                    </option>
                  ))}
                </Select>
                {layoutTemplateId ? (
                  <p className="text-xs text-muted-foreground">
                    {
                      layoutTemplateOptions.find(
                        (template) => template.templateId === layoutTemplateId,
                      )?.description
                    }
                  </p>
                ) : null}
                {layoutTemplatesLoading ? (
                  <p className="text-xs text-muted-foreground">Loading published templates...</p>
                ) : null}
                {layoutTemplatesError ? (
                  <Notice tone="danger">{layoutTemplatesError}</Notice>
                ) : null}
                {!layoutTemplatesLoading && templateModeUnavailable ? (
                  <Notice tone="warning">
                    No compatible published layout templates are available for this post type.
                  </Notice>
                ) : null}
              </div>
            ) : null}

            {generationMode === "freestyle" ? (
              <Notice tone="info">
                Freestyle skips published layout templates and asks the planner to create the scene
                directly.
              </Notice>
            ) : null}
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium" htmlFor="planner-prompt">
              Prompt
            </label>
            <InfoTooltip
              label="Explain prompt"
              content="Describe the creative direction for this one attempt. The planner still uses the brand profile, Content DNA facts, assets, and selected target format."
            />
          </div>
          <Textarea
            id="planner-prompt"
            value={prompt}
            onChange={(event) => onPromptChange(event.target.value)}
            rows={8}
            className="min-h-36"
            placeholder="Describe the post angle, tension, mechanic, and CTA for this planner attempt."
          />
          <p className="text-xs text-muted-foreground">
            Keep prompts short and specific. Mention product angle, audience tension,
            platform-native mechanic, tone, and CTA.
          </p>
        </div>

        {brandStatus !== "scanned" ? (
          <Notice tone="warning">
            This brand is currently <span className="font-medium">{brandStatus}</span>. Generation
            attempts are only enabled once scanning is complete.
          </Notice>
        ) : null}

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <Button onClick={onSubmit} disabled={submitDisabled} className="w-full">
          {isPending ? (
            <>
              <LoaderCircle className="size-4 animate-spin" />
              Queueing generation attempt...
            </>
          ) : (
            <>
              <Sparkles className="size-4" />
              Queue Generation Attempt
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
